/**
 * useLiveMeter — the /desktop live meter's facts (usage-metering step 11, 2026-10-20): the signed-in person's own
 * conversation (accounts/{id}/live_meters/{uid}) and the workspace's awake time (accounts/{id}/vm/main.vmAwake), read
 * as they change, with a clock that runs each second while a conversation is going and each minute otherwise. What the
 * chip says is liveMeterModel.ts's. A read that fails, or a person not signed in, shows nothing: the page as before.
 */
import { useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';
import { ACCOUNT_FIRESTORE_PREFIX } from '../services/prest0nRuntime/constants';
import {
  conversationOpen, liveMeterAwakeOf, liveMeterConversationOf, liveMeterView,
  type LiveMeterAwake, type LiveMeterConversation, type LiveMeterView,
} from './liveMeterModel';

/**
 * The meter for the workspace whose workstation is on screen (`accountId`; null for none). `awakeNow`: that machine is
 * awake, so its time since the sweep's last count runs on.
 */
export function useLiveMeter(accountId: string | null, awakeNow: boolean): LiveMeterView | null {
  const [conversation, setConversation] = useState<LiveMeterConversation | null>(null);
  const [awake, setAwake] = useState<LiveMeterAwake | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    setConversation(null);
    let uid: string | null = null;
    try { uid = getAuth().currentUser?.uid ?? null; } catch { uid = null; }
    if (!accountId || !uid) return undefined;
    let unsub: (() => void) | null = null;
    try {
      unsub = onSnapshot(
        doc(getFirestore(getApp()), ACCOUNT_FIRESTORE_PREFIX, accountId, 'live_meters', uid),
        (snap) => setConversation(snap.exists() ? liveMeterConversationOf(snap.data()) : null),
        () => setConversation(null),
      );
    } catch {
      setConversation(null);
    }
    return () => { if (unsub) unsub(); };
  }, [accountId]);

  useEffect(() => {
    setAwake(null);
    if (!accountId) return undefined;
    let unsub: (() => void) | null = null;
    try {
      unsub = onSnapshot(
        doc(getFirestore(getApp()), ACCOUNT_FIRESTORE_PREFIX, accountId, 'vm', 'main'),
        (snap) => setAwake(snap.exists() ? liveMeterAwakeOf(snap.data()) : null),
        () => setAwake(null),
      );
    } catch {
      setAwake(null);
    }
    return () => { if (unsub) unsub(); };
  }, [accountId]);

  const going = conversation ? conversationOpen(conversation, nowMs) : false;
  const anything = Boolean(conversation || awake);
  useEffect(() => {
    if (!anything) return undefined;
    setNowMs(Date.now());
    const timer = setInterval(() => setNowMs(Date.now()), going ? 1_000 : 60_000);
    return () => clearInterval(timer);
  }, [going, anything]);

  return useMemo(() => liveMeterView({ conversation, awake, awakeNow, nowMs }), [conversation, awake, awakeNow, nowMs]);
}
